/**
 * Airflow 연동 FastAPI(BE1) 기본 주소.
 *
 * 우선순위: REACT_APP_BE1_API_URL(.env) → 브라우저가 접속한 호스트명의 8001 포트.
 * 'localhost' 를 하드코딩하면 다른 PC 에서 http://192.168.0.5:3001 로 열었을 때 그 PC 의 localhost 를 찾아
 * ERR_CONNECTION_REFUSED 가 난다. 호스트명에서 유도하면 어느 주소로 접속하든 같은 서버의 백엔드로 간다.
 */
// '/' 를 주면 같은 오리진(상대경로)으로 호출한다 — 스탠드얼론 이미지처럼 프론트와 API 가 한 서버일 때.
// 빈 문자열 판정을 '설정되지 않음' 과 구분해야 한다(빈 문자열은 falsy 라 예전 코드는 :8001 로 폴백했다).
// eslint-disable-next-line import/first -- eventLog 가 BE1_API 를 쓰므로 순환이지만, 양쪽 다 호출 시점에만 참조한다
import { logError } from '../services/eventLog';
import i18n from '../i18n';
import { notifyUnauthorized } from '../services/sessionExpiry';
import { noteResponse as noteMaintenance } from '../services/maintenance';

const _be1Raw = process.env.REACT_APP_BE1_API_URL;
export const BE1_API: string =
  _be1Raw !== undefined && _be1Raw.trim() !== ''
    ? _be1Raw.trim().replace(/\/+$/, '')   // 'http://host:8001/' → 'http://host:8001', '/' → ''(상대경로)
    : (typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.hostname}:8001` : 'http://localhost:8001');

/**
 * BE1 호출 공통 — 네트워크 오류(백엔드 재시작 중 ERR_CONNECTION_REFUSED 등)면 잠시 뒤 재시도하고,
 * 그래도 안 되면 어느 주소에 연결하지 못했는지 알 수 있는 메시지로 바꿔 던진다.
 */
export async function be1Fetch(path: string, init?: RequestInit, opts: { retries?: number; delayMs?: number } = {}): Promise<Response> {
  const { retries = 2, delayMs = 1500 } = opts;
  const url = `${BE1_API}${path}`;
  let lastErr: unknown;
  for (let i = 0; i <= retries; i++) {
    try {
      const res = await fetch(url, init);
      // 로그인이 끊겼다 — 부르는 쪽마다 따로 다루지 않고 한 곳에서 알린다 (services/sessionExpiry)
      if (res.status === 401) notifyUnauthorized(path);
      // 큐베이스 점검 중이면 BE1 도 X-QBase-Maintenance 헤더·503 MAINTENANCE 로 알린다 — 화면 위 띠로 (services/maintenance).
      // 본문은 부르는 쪽이 읽으므로 복제본으로 본다. 막힌 요청을 여기서 다시 보내지 않는다.
      else noteMaintenance(res, res.status >= 500 ? await res.clone().json().catch(() => undefined) : undefined);
      return res;
    } catch (e) {
      lastErr = e;
      if (i < retries) await new Promise((r) => setTimeout(r, delayMs));
    }
  }
  const shown = BE1_API || (typeof window !== 'undefined' ? window.location.origin : '');  // 상대경로면 현재 오리진
  const err = new Error(i18n.t('common:error.backendUnreachable', { url: shown, message: (lastErr as Error)?.message || String(lastErr) }));
  // 요청이 서버에 닿지도 못한 경우다 — 서버 미들웨어는 이것을 볼 수 없으므로 화면 쪽에서 남긴다.
  // (HTTP 4xx/5xx 는 서버가 이미 남기므로 여기서 또 남기지 않는다 — 같은 사건이 두 줄이 된다)
  logError('api.failed', err, { where: path, target: `${init?.method || 'GET'} ${path}` });
  throw err;
}
